import {
  Question,
  QA,
  MFR,
  Product,
  ProductWithPSC,
  ProductWithCodes,
  Vendor,
  ApiResponse,
} from '../types/pipeline';
import { apiCall, delay } from './apiClient';
import mockPscOutput from '../mockData/psc-output.json';
import { ApiResponse as RawApiResponse } from './apiClient';

// Mock data configuration
const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK === 'true';
const MOCK_UP_TO_STAGE = parseInt(process.env.REACT_APP_MOCK_UP_TO_STAGE || '0');

// Helper to determine if a stage should use mock data
const shouldUseMock = (stageNumber: number): boolean => {
  if (USE_MOCK_DATA) return true;
  return stageNumber <= MOCK_UP_TO_STAGE;
};

// Stage 1: Requirements Elicitation Functions

// Save answers immediately for persistence
export async function saveAnswers(
  flowId: string,
  questions: Question[],
  answers: Record<string, string>
): Promise<ApiResponse<{ answers_saved: number; questions_count: number }>> {
  try {
    const response = await apiCall<{ answers_saved: number; questions_count: number }>(
      '/api/requirements/save-answers',
      'POST',
      {
        flow_id: flowId,
        questions: questions,
        answers: answers
      }
    );
    
    return {
      success: response.success,
      data: response.data || { answers_saved: 0, questions_count: 0 },
      timestamp: new Date().toISOString(),
      error: response.error
    };
  } catch (error) {
    console.error('Failed to save answers:', error);
    return {
      success: false,
      data: { answers_saved: 0, questions_count: 0 },
      error: 'Failed to save answers',
      timestamp: new Date().toISOString()
    };
  }
}

export async function getRequirementQuestions(
  commodityDescription: string,
  flowId?: string | null,
  forceRecompute?: boolean
): Promise<ApiResponse<Question[]>> {
  // Try to use real API first
  if (!shouldUseMock(1)) {
    try {
      const response = await apiCall<Question[]>(
        '/api/requirements/questions',
        'POST',
        { 
          commodity_description: commodityDescription,
          flow_id: flowId,
          force_recompute: forceRecompute 
        }
      );
      if (response.success) {
        return {
          ...response,
          timestamp: new Date().toISOString()
        };
      }
    } catch (error) {
      console.warn('API call failed, falling back to mock data:', error);
    }
  }

  // Fall back to mock data
  await delay(1500); // Simulate API call
  
  // Generate questions based on commodity description
  const questions: Question[] = [
    {
      id: '1',
      category: 'features',
      question: `What specific tasks will this ${commodityDescription.split(' ')[0]} support?`,
      priority: 'High',
      helpText: 'Describe the primary use cases and operational scenarios',
    },
    {
      id: '2',
      category: 'performance',
      question: 'What are the minimum performance specifications required?',
      priority: 'High',
      helpText: 'Include metrics like speed, capacity, range, etc.',
    },
    {
      id: '3',
      category: 'performance',
      question: 'What environmental conditions must the system operate in?',
      priority: 'Medium',
      helpText: 'Temperature ranges, humidity, altitude, etc.',
    },
    {
      id: '4',
      category: 'compatibility',
      question: 'What existing systems must this integrate with?',
      priority: 'High',
      helpText: 'List current infrastructure and compatibility requirements',
    },
    {
      id: '5',
      category: 'features',
      question: 'Are there specific compliance or certification requirements?',
      priority: 'Medium',
      helpText: 'Military standards, industry certifications, etc.',
    },
    {
      id: '6',
      category: 'features',
      question: 'What level of vendor support and training is required?',
      priority: 'Low',
      helpText: 'Maintenance, training, documentation needs',
    },
  ];
  
  return {
    success: true,
    data: questions,
    timestamp: new Date().toISOString(),
  };
}

export async function generateMinimalRequirements(
  questionsAndAnswers: QA[],
  commodityDescription?: string,
  flowId?: string | null
): Promise<ApiResponse<MFR>> {
  console.log('[stageFunctions] generateMinimalRequirements called');
  console.log('[stageFunctions] shouldUseMock(2):', shouldUseMock(2));
  console.log('[stageFunctions] commodityDescription:', commodityDescription);
  console.log('[stageFunctions] flowId:', flowId);
  // Try to use real API first (now Stage 2 for MFR generation)
  if (!shouldUseMock(2) && commodityDescription) {
    try {
      console.log('[stageFunctions] Calling POST /api/requirements/generate-mfr');
      const response = await apiCall<any>(
        '/api/requirements/generate-mfr',
        'POST',
        {
          commodity_description: commodityDescription,
          questions_and_answers: questionsAndAnswers.map(qa => ({
            question: qa,
            answer: qa.answer
          })),
          flow_id: flowId
        }
      );
      if (response.success && response.data) {
        // Parse the minimum requirements text into structured format
        const mfrText = response.data.minimum_requirements || '';
        
        // Split the MFR text into individual requirements
        // Look for numbered patterns like "1. " or "2. "
        const reqPattern = /\d+\.\s\*\*[^:]+:\*\*/g;
        const parts = mfrText.split(reqPattern);
        
        // Extract the requirement titles and content
        const matches = mfrText.match(reqPattern) || [];
        const requirements: any = {
          functional: [],
          performance: [],
        };
        
        // If we have numbered requirements, parse them
        if (matches.length > 0) {
          matches.forEach((match: string, index: number) => {
            if (parts[index + 1]) {
              // Clean up the requirement text
              const reqTitle = match.replace(/\d+\.\s\*\*/, '').replace(/:\*\*/, '');
              const reqContent = parts[index + 1].split('REMOVED OVERSPECIFICATIONS')[0].trim();
              requirements.functional.push(`${reqTitle}: ${reqContent}`);
            }
          });
        } else if (mfrText.trim()) {
          // Fallback: split by sentences that look like requirements
          const sentences = mfrText.split(/(?<=[.!?])\s+/);
          requirements.functional = sentences.filter((s: string) => s.length > 20);
        }
        
        // Try to extract a title from the MFR text
        let title = commodityDescription?.substring(0, 100) || 'System Requirements';
        
        // Look for a title in the MFR text (usually starts with ## or ** and contains "Requirements")
        const titleMatch = mfrText.match(/##\s*(.+Requirements.+)/i) || 
                          mfrText.match(/\*\*(.+Requirements.+)\*\*/i);
        if (titleMatch) {
          title = titleMatch[1].trim();
        }
        
        const mfr: MFR = {
          id: `mfr-${Date.now()}`,
          commodityDescription: title,  // Use the extracted title instead of full description
          generatedAt: new Date().toISOString(),
          requirements: requirements,
          questionsAnswered: questionsAndAnswers,
          rawText: mfrText,
          originalCommodityDescription: commodityDescription,  // Store original separately
        };
        return {
          success: true,
          data: mfr,
          timestamp: new Date().toISOString()
        };
      }
    } catch (error) {
      console.warn('API call failed, falling back to mock data:', error);
    }
  }
  
  await delay(2000); // Simulate processing
  
  // Group answers by category
  const functional = questionsAndAnswers
    .filter(qa => qa.category === 'Functional Requirements')
    .map(qa => qa.answer);
  
  const performance = questionsAndAnswers
    .filter(qa => qa.category === 'Performance')
    .map(qa => qa.answer);
  
  const environmental = questionsAndAnswers
    .filter(qa => qa.category === 'Environmental')
    .map(qa => qa.answer);
  
  const integration = questionsAndAnswers
    .filter(qa => qa.category === 'Integration')
    .map(qa => qa.answer);
  
  const mfr: MFR = {
    id: `mfr-${Date.now()}`,
    commodityDescription: questionsAndAnswers[0]?.answer || 'System',
    generatedAt: new Date().toISOString(),
    requirements: {
      functional,
      performance,
      environmental: environmental.length > 0 ? environmental : undefined,
      integration: integration.length > 0 ? integration : undefined,
    },
    questionsAnswered: questionsAndAnswers,
    rawText: `MINIMAL FUNCTIONAL REQUIREMENTS\n\n${functional.join('\n')}\n\n${performance.join('\n')}`,
  };
  
  return {
    success: true,
    data: mfr,
    timestamp: new Date().toISOString(),
  };
}

// Helper function to transform pipeline products to UI format
function transformPipelineProduct(pipelineProduct: any, index: number): Product {
  // Generate ID if not present
  const id = pipelineProduct.id || `product-${index}`;
  
  // Extract source from evidence
  const sourceMapping: Record<string, 'GSA' | 'Commercial' | 'Government' | 'Both'> = {
    usaspending: 'Government',
    gsa: 'GSA',
    gsa_advantage: 'GSA',
    commercial: 'Commercial'
  };
  const evidenceSource = pipelineProduct.evidence?.source?.toLowerCase() || 'commercial';
  const uiSource = sourceMapping[evidenceSource] || 'Commercial';
  
  // Note: matchScore removed as it's redundant - products already passed relevance filtering
  
  // Extract key specs from specifications object
  const keySpecs: string[] = [];
  if (pipelineProduct.specifications) {
    Object.entries(pipelineProduct.specifications).forEach(([key, value]) => {
      if (value && key !== 'award_id' && key !== 'piid') {
        if (Array.isArray(value)) {
          keySpecs.push(`${key}: ${value.join(', ')}`);
        } else if (typeof value === 'string' && value.trim()) {
          keySpecs.push(`${key}: ${value}`);
        }
      }
    });
  }
  
  // Normalize evidence for display: prefer concise labels in UI column
  const normalizedEvidence = ((): any => {
    const ev = pipelineProduct.evidence ? { ...pipelineProduct.evidence } : { source: evidenceSource };
    if (typeof ev.source === 'string') {
      const s = ev.source.toLowerCase();
      if (s === 'commercial_web_search') {
        ev.source = 'web_search';
      }
    }
    return ev;
  })();

  return {
    id,
    // Pipeline fields
    product_name: pipelineProduct.product_name || pipelineProduct.name || 'Unknown Product',
    manufacturer: pipelineProduct.manufacturer || 'Unknown',
    model: pipelineProduct.model || '',
    specifications: pipelineProduct.specifications || {},
    evidence: normalizedEvidence,
    source_type: pipelineProduct.source_type,
    meets_requirements: pipelineProduct.meets_requirements || false,
    relevance_check: pipelineProduct.relevance_check,
    
    // Legacy computed fields for UI compatibility
    name: pipelineProduct.product_name || pipelineProduct.name || 'Unknown Product',
    keySpecs,
    source: uiSource,
    contractNumber: pipelineProduct.specifications?.award_id || pipelineProduct.specifications?.piid,
    included: false, // Default UI state
    price: pipelineProduct.specifications?.award_amount || pipelineProduct.price
  };
}

// Stage 3: Product Discovery Functions
export async function findMatchingProducts(
  mfr: MFR,
  flowId?: string | null,
  forceRecompute?: boolean
): Promise<ApiResponse<Product[]>> {
  console.log('findMatchingProducts called with MFR:', mfr);
  console.log('Mock settings - USE_MOCK_DATA:', USE_MOCK_DATA, 'MOCK_UP_TO_STAGE:', MOCK_UP_TO_STAGE);
  console.log('shouldUseMock(3):', shouldUseMock(3));
  
  // Try to use real API first
  if (!shouldUseMock(3)) {
    try {
      console.log('Calling real API for product search...');
      // Normalize required MFR fields to satisfy backend Pydantic model
      const normalizedRequirements = {
        functional: mfr.requirements?.functional || [],
        performance: mfr.requirements?.performance || [],
        environmental: mfr.requirements?.environmental || [],
        integration: mfr.requirements?.integration || [],
        other: mfr.requirements?.other || [],
      };

      const payload = {
        // Backend expects FastAPI model MFR (see backend/models.py)
        id: mfr.id, // Use stable ID from Stage 2
        commodityDescription: mfr.originalCommodityDescription || mfr.commodityDescription || '',
        requirements: normalizedRequirements,
        generatedAt: mfr.generatedAt, // Do not default to now to keep hash stable
        version: (mfr as any).version || '1.0',
        questionsAnswered: mfr.questionsAnswered || [],
        rawText: mfr.rawText || '',
        originalCommodityDescription: mfr.originalCommodityDescription || mfr.commodityDescription || '',
        flow_id: flowId,
        force_recompute: !!forceRecompute,
      } as any;
      console.log('Stage3 payload keys:', Object.keys(payload), 'rawText.len:', (payload.rawText||'').length);
      const response = await apiCall<any>(
        '/api/products/search',
        'POST',
        payload,
        { retries: 0 }
      );
      console.log('API response:', response);
      console.log('API response.data:', response.data);
      console.log('API response.data?.products:', response.data?.products);
      
      if (response.success) {
        // Transform pipeline format to UI format
        const pipelineProducts = response.data?.products || response.data || [];
        // Optionally: source_stats available at response.data?.source_stats
        console.log('Pipeline products extracted:', pipelineProducts);
        console.log('Pipeline products is array?', Array.isArray(pipelineProducts));
        console.log('Pipeline products length:', pipelineProducts.length);
        
        // Handle empty results properly - don't fall back to mock
        if (!pipelineProducts || pipelineProducts.length === 0) {
          console.log('No products found, returning empty array');
          return {
            success: true,
            data: [],  // Return empty array, not mock data
            timestamp: new Date().toISOString()
          };
        }
        
        const transformedProducts: Product[] = pipelineProducts.map(transformPipelineProduct);
        console.log('Transformed products:', transformedProducts);
        
        return {
          success: true,
          data: transformedProducts,
          timestamp: new Date().toISOString()
        };
      } else {
        console.log('API returned success: false');
        // If API explicitly returns success: false, don't fall back to mock
        return {
          success: false,
          data: [],
          error: response.error || 'Product search failed',
          timestamp: new Date().toISOString()
        };
      }
    } catch (error) {
      console.error('API call failed:', error);
      // Return error response instead of falling back to mock
      return {
        success: false,
        data: [],
        error: 'Failed to search for products. Please try again.',
        timestamp: new Date().toISOString()
      };
    }
  } else {
    // Only use mock data when explicitly configured to do so
    console.log('Using mock data for Stage 3');
    await delay(2500); // Simulate search
    
    // Load mock products from JSON file
    console.log('Loading mock products from JSON file...');
    const mockProductsModule = await import('../mockData/products-output.json');
    const mockProductsData = mockProductsModule.default || mockProductsModule;
    
    // Transform the products from the JSON file
    const pipelineProducts = mockProductsData.products || [];
    const products: Product[] = pipelineProducts.map(transformPipelineProduct);
    
    return {
      success: true,
      data: products,
      timestamp: new Date().toISOString(),
    };
  }
}

// Stage progress (polling)
export async function getStageProgress(
  flowId: string,
  stageNum: number
): Promise<{ messages: { timestamp: string; message: string }[]; done: boolean } | null> {
  // Use apiCall (returns raw JSON body), but normalize to a simple shape.
  const resp = await apiCall<any>(`/api/flows/${flowId}/stage/${stageNum}/progress`, 'GET');
  // Backend returns raw shape: { messages, done }
  if (resp && (resp as any).messages) {
    return resp as any;
  }
  // If someone wraps it in ApiResponse later, handle that too
  if ((resp as any)?.success && (resp as any)?.data) {
    return (resp as any).data;
  }
  return null;
}

export async function resetStageProgress(flowId: string, stageNum: number): Promise<boolean> {
  const resp = await apiCall(`/api/flows/${flowId}/stage/${stageNum}/progress/reset`, 'POST', {});
  if (resp && typeof resp.success === 'boolean') {
    return resp.success;
  }
  return false;
}

// Stage 4: PSC Code Assignment Functions
export async function assignPSCCodes(
  products: Product[],
  flowId?: string | null,
  forceRecompute: boolean = false,
): Promise<ApiResponse<ProductWithPSC[]>> {
  // Try to use real API first
  if (!shouldUseMock(4)) {
    try {
      const response = await apiCall<ProductWithPSC[]>(
        '/api/psc/assign',
        'POST',
        { products, flow_id: flowId, force_recompute: !!forceRecompute }
      );
      if (response.success) {
        return {
          ...response,
          timestamp: new Date().toISOString()
        };
      }
    } catch (error) {
      console.warn('API call failed, falling back to mock data:', error);
    }
  }
  
  // Fall back to mock data - use actual JSON file exactly as is
  await delay(1500); // Simulate processing
  
  // Use the actual products_with_psc from the JSON file directly
  const productsWithPSC = mockPscOutput.products_with_psc as any[];
  
  return {
    success: true,
    data: productsWithPSC,
    timestamp: new Date().toISOString(),
  };
}

// Save edited PSC assignment to cache (Stage 4)
export async function savePSCAssignment(
  productsWithPSC: any[],
  flowId?: string | null,
): Promise<ApiResponse<any[]>> {
  if (!flowId) {
    return { success: false, error: 'flowId is required' } as any;
  }
  try {
    const response = await apiCall<any[]>(
      '/api/psc/save',
      'POST',
      { flow_id: flowId, products_with_psc: productsWithPSC }
    );
    return response as any;
  } catch (error) {
    return handleApiError(error);
  }
}

// Stage 5: NAICS Code Mapping Functions
export async function mapNAICSCodes(
  products: ProductWithPSC[],
  flowId?: string | null,
  forceRecompute: boolean = false
): Promise<ApiResponse<ProductWithCodes[]>> {
  // Check if we should use mock data for this stage
  if (!shouldUseMock(5)) {
    try {
      // Call real backend API
      const response = await apiCall<ProductWithCodes[]>(
        '/api/naics/map',
        'POST',
        { products, flow_id: flowId, force_recompute: !!forceRecompute }  // Send as object with products array
      );
      
      // Transform the response to ensure UI compatibility
      // The script returns naics_from_psc as array of strings
      if (response.success && response.data) {
        const transformedData = response.data.map(product => ({
          ...product,
          // Add legacy fields for UI if needed
          naicsCode: product.naics_from_psc?.[0] || product.naicsCode || '',
          naicsDescription: product.naicsDescription || 'NAICS code from PSC mapping',
          naicsConfidence: product.naicsConfidence || 90,
          alternativeNAICS: product.alternativeNAICS || [],
          historicalUsage: product.historicalUsage || 0,
        }));
        
        return {
          success: true,
          data: transformedData,
          timestamp: new Date().toISOString(),
        };
      }
    } catch (error) {
      console.error('Error mapping NAICS codes:', error);
      // Fall back to mock data on error
    }
  }
  
  // Mock data fallback - load actual script output
  await delay(1500); // Simulate processing
  
  // Load the actual script output sample
  const mockNaicsOutput = await import('../mockData/naics-output.json');
  
  // Merge the products with PSC from previous stage with NAICS data
  const productsWithCodes: ProductWithCodes[] = products.map((product, index) => {
    // Get corresponding product from mock output if available
    const mockProduct = mockNaicsOutput.products_with_naics[index] || mockNaicsOutput.products_with_naics[0];
    
    return {
      ...product,
      // Preserve all fields from the product (including PSC codes from stage 3)
      // Add fields from the actual script output
      ...mockProduct,
      // Ensure ID is preserved
      id: product.id,
      // Script output structure
      naics_from_psc: mockProduct.naics_from_psc || [],
      psc_codes_api: mockProduct.psc_codes_api || [],
      // Legacy fields for UI compatibility (computed from script output)
      naicsCode: mockProduct.naics_from_psc?.[0] || '',
      naicsDescription: `NAICS ${mockProduct.naics_from_psc?.[0] || 'N/A'}`,
    };
  });
  
  return {
    success: true,
    data: productsWithCodes,
    timestamp: new Date().toISOString(),
  };
}

// Stage 6: Vendor Discovery Functions
export async function searchVendors(
  products: ProductWithCodes[],
  flowId?: string | null,
  forceRecompute: boolean = false
): Promise<ApiResponse<any[]>> {
  // Check if we should use mock data for this stage
  if (!shouldUseMock(6)) {
    try {
      // Call real backend API
      const response = await apiCall<any[]>(
        '/api/vendors/search',
        'POST',
        { products, flow_id: flowId, force_recompute: !!forceRecompute }  // Send as object with products array
      );
      
      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,  // products_with_vendors array
          timestamp: new Date().toISOString(),
        };
      }
    } catch (error) {
      console.error('Error searching vendors:', error);
      // Fall back to mock data on error
    }
  }
  
  // Mock data fallback - load actual script output
  await delay(1500); // Simulate processing
  
  // Load the actual script output sample
  const mockVendorsOutput = await import('../mockData/vendors-output.json');
  
  // Merge the products with vendor data
  const productsWithVendors = products.map((product, index) => {
    // Get corresponding product from mock output if available
    const mockProduct = mockVendorsOutput.products_with_vendors[index] || 
                       mockVendorsOutput.products_with_vendors[0];
    
    return {
      ...product,
      // Add vendor array from the script output
      vendors: mockProduct.vendors || []
    };
  });
  
  return {
    success: true,
    data: productsWithVendors,
    timestamp: new Date().toISOString(),
  };
}

// Legacy vendor search function (deprecated - kept for backward compatibility)
export async function searchVendorsLegacy(
  products: ProductWithCodes[]
): Promise<ApiResponse<Vendor[]>> {
  await delay(3000); // Simulate vendor search
  
  const vendors: Vendor[] = [
    {
      id: 'vendor-1',
      name: 'Raytheon Technologies Corporation',
      cageCode: '49956',
      dunsNumber: '001234567',
      socioeconomicStatus: ['Large Business'],
      naicsCodes: ['334511', '334290'],
      pscCodes: ['5840', '5841'],
      pastPerformance: {
        rating: 4.8,
        contractCount: 156,
        totalValue: '$2.3B',
      },
      capabilities: [
        'Radar Systems Design',
        'Systems Integration',
        'Technical Support',
        'Training Services',
      ],
      certifications: [
        'ISO 9001:2015',
        'AS9100D',
        'CMMI Level 5',
      ],
      contactInfo: {
        email: 'gov.contracts@rtx.com',
        phone: '781-522-3000',
        address: '870 Winter Street, Waltham, MA 02451',
        website: 'https://www.rtx.com',
      },
      samRegistered: true,
      productsOffered: ['AN/SPS-48E', 'AN/SPY-1', 'SeaRAM'],
    },
    {
      id: 'vendor-2',
      name: 'Advanced Defense Systems LLC',
      cageCode: '7B4K9',
      socioeconomicStatus: ['Small Business', 'Woman-Owned Small Business'],
      naicsCodes: ['334511', '541330'],
      pscCodes: ['5840'],
      pastPerformance: {
        rating: 4.5,
        contractCount: 23,
        totalValue: '$45M',
      },
      capabilities: [
        'Radar Components',
        'Integration Services',
        'Maintenance Support',
      ],
      certifications: [
        'ISO 9001:2015',
      ],
      contactInfo: {
        email: 'contracts@advdefense.com',
        phone: '703-555-0123',
        address: '1234 Defense Way, Arlington, VA 22202',
      },
      samRegistered: true,
      productsOffered: ['Radar Components', 'Integration Services'],
    },
    {
      id: 'vendor-3',
      name: 'Tactical Systems International',
      cageCode: '5M8N2',
      socioeconomicStatus: ['Small Business', 'Service-Disabled Veteran-Owned'],
      naicsCodes: ['334511'],
      pscCodes: ['5840', '5845'],
      pastPerformance: {
        rating: 4.3,
        contractCount: 18,
        totalValue: '$28M',
      },
      capabilities: [
        'System Maintenance',
        'Technical Documentation',
        'Field Support',
      ],
      certifications: [
        'ISO 9001:2015',
        'ISO 27001',
      ],
      contactInfo: {
        email: 'info@tacticalsys.com',
        phone: '571-555-0456',
        address: '5678 Military Road, Falls Church, VA 22041',
      },
      samRegistered: true,
      productsOffered: ['Maintenance Services', 'Technical Support'],
    },
  ];
  
  return {
    success: true,
    data: vendors,
    timestamp: new Date().toISOString(),
  };
}

// Stage 7: Section B Generation Functions
export async function generateSectionB(
  productsWithVendors: any[],
  flowId?: string | null,
  forceRecompute: boolean = false
): Promise<ApiResponse<string>> {
  // Check if we should use mock data for this stage
  if (!shouldUseMock(7)) {
    try {
      // Call real backend API
      const response = await apiCall<string>(
        '/api/section-b/generate',
        'POST',
        {
          flow_id: flowId,
          products_with_vendors: productsWithVendors,
          force_recompute: forceRecompute,
          // Include only stable, optional fields when specified by UI. Defaults handled server-side.
          // quantities, delivery_date, contract_type can be added here when UI supports edits.
        }
      );
      
      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,  // Section B markdown content
          timestamp: new Date().toISOString(),
        };
      }
    } catch (error) {
      console.error('Error generating Section B:', error);
      // Fall back to mock data on error
    }
  }
  
  // Mock data fallback - load actual Section B output
  await delay(1500); // Simulate processing
  
  // Load the actual script output sample
  // Note: We need to load the markdown file as text
  try {
    const response = await fetch('/mockData/section-b-output.md');
    const mockSectionB = await response.text();
    
    return {
      success: true,
      data: mockSectionB,
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    // If we can't load the file, return a fallback
    const fallbackContent = `# SECTION B - SUPPLIES OR SERVICES AND PRICES/COSTS

## Contract Line Items

| CLIN | Item Description | Quantity | Unit | Unit Price | Total Price |
|------|-----------------|----------|------|------------|-------------|
| 0001 | Trimble R12i GNSS System | 1 | EA | TBD | TBD |
| 0002 | Surveying Equipment | 1 | EA | TBD | TBD |
| 0003 | TRIMBLE 12I GNSS SYSTEM | 1 | EA | TBD | TBD |

## Notes
- Prices to be determined through competitive bidding
- Delivery: 90 days ARO
- Contract Type: FFP (Firm Fixed Price)

*Generated from vendor discovery data*`;
    
    return {
      success: true,
      data: fallbackContent,
      timestamp: new Date().toISOString(),
    };
  }
}

// Utility function to handle API errors
export function handleApiError(error: any): ApiResponse<any> {
  return {
    success: false,
    error: error.message || 'An error occurred',
    timestamp: new Date().toISOString(),
  };
}
